export const MAX_TICKET_RECIPIENTS = 25;

export type StorefrontEnvironment = 'test' | 'live';
export type ConsumableMode = 'none' | 'stamps' | 'points' | 'credits';

export interface StorefrontClientConfiguration {
  apiBaseUrl: string;
  templateId: number;
  environment?: StorefrontEnvironment;
}

export interface RequestOptions {
  signal?: AbortSignal;
}

export interface EventSummary {
  id: number;
  name: string;
}

export interface TicketConsumable {
  enabled: boolean;
  mode: ConsumableMode;
  currency: string;
  maximum: number | null;
  valuePerUnit: number | null;
}

export interface TicketType {
  id: string;
  name: string;
  price: string;
  currency: string;
  consumable: TicketConsumable;
}

export interface StorefrontCatalog {
  event: EventSummary;
  environment: StorefrontEnvironment;
  ticketTypes: TicketType[];
}

export interface TicketRecipient {
  name: string;
  email: string;
  ticketTypeProductId: string;
}

export interface CheckoutRedirects {
  successUrl: string;
  cancelUrl: string;
}

export interface TicketCheckoutInput {
  recipients: TicketRecipient[];
  buyerEmail?: string;
  redirects?: CheckoutRedirects;
  returnUrl?: string | URL;
}

export interface RechargeSelection {
  email: string;
  selectionToken: string;
  priceMappingId: string;
  quantity?: number;
}

export interface RechargeCheckoutInput extends RechargeSelection {
  redirects?: CheckoutRedirects;
  returnUrl?: string | URL;
}

export interface RechargeOption {
  priceMappingId: string;
  amount: number;
  maxQuantity: number;
  available: boolean;
}

export interface RechargeCardholder {
  selectionToken: string;
  name: string;
  ticketType: string;
  eventName: string;
  consumable: Exclude<ConsumableMode, 'none'>;
  currency: string;
  options: RechargeOption[];
}

export interface RechargeLookupResult {
  cardholders: RechargeCardholder[];
}

export interface PreflightResult {
  valid: boolean;
  [key: string]: unknown;
}

export interface CheckoutResult {
  checkoutUrl: string;
  [key: string]: unknown;
}

export interface StorefrontContext {
  templateId: number;
  environment: StorefrontEnvironment;
}

export interface StorefrontClient {
  readonly context: Readonly<StorefrontContext>;
  getCatalog(options?: RequestOptions): Promise<StorefrontCatalog>;
  preflightTickets(
    recipients: TicketRecipient[],
    options?: RequestOptions,
  ): Promise<PreflightResult>;
  createTicketCheckout(
    input: TicketCheckoutInput,
    options?: RequestOptions,
  ): Promise<CheckoutResult>;
  startTicketCheckout(
    input: TicketCheckoutInput,
    options?: RequestOptions,
  ): Promise<CheckoutResult>;
  lookupRecharge(
    email: string,
    options?: RequestOptions,
  ): Promise<RechargeLookupResult>;
  preflightRecharge(
    selection: RechargeSelection,
    options?: RequestOptions,
  ): Promise<PreflightResult>;
  createRechargeCheckout(
    input: RechargeCheckoutInput,
    options?: RequestOptions,
  ): Promise<CheckoutResult>;
  startRechargeCheckout(
    input: RechargeCheckoutInput,
    options?: RequestOptions,
  ): Promise<CheckoutResult>;
}
